import moment from 'moment'
import {FC, useEffect, useState} from 'react'
import {Button, Dropdown, OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import { useParams, useSearchParams } from 'react-router-dom'
import useAppToast from 'src/hooks/useAppToast'
import {
  getAllInventoryLocationsAsync,
  getInventoryLogsAsync,
  resyncFailedInventoryAsync,
} from 'src/redux/actions/inventoryActions'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import AppDatePickerRange from '../orders/components/AppDatePickerRange'
import { formatDateLocal, formatTimeZone } from 'src/utils/TimeZoneDateFormat'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faInfoCircle, faRotate } from '@fortawesome/free-solid-svg-icons'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import StatusButton from '../orders/StatusButton'


const ColProductName: FC<{
  name: string
}> = ({name}) => {
  return (
    <>
      <div className='d-flex align-items-center'>
        <div className='ms-2'>
          <p className='mb-0'>
            <Button
              variant='link'
              className=' fw-bold fs-5 link-underline-primary py-0 text-primary text-decoration-underline mb-0'
            >
              {name}
            </Button>
          </p>
          {/* <p className='h6 text-muted'>{name.subtitle}</p> */}
        </div>
      </div>
    </>
  )
}

interface InventoryLogsProps {
  isProductDetailPage?: boolean; // optional prop
  variantId?: string | number;
}

const CommonColumn: FC<{rowData: string}> = ({rowData}) => {
  return (
    <>
      <p>{rowData}</p>
    </>
  )
}

const sourcePlatformRoleValues = new Set([
  'PRIMARY',
  'MAIN',
  'MAIN_STORE',
  'SOURCE',
  'ACCOUNTING',
  'SHIPPING',
  'ERP',
  'OMS',
  'INVENTORY',
  'PRICING',
  'BILLING',
  'TICKET',
])

const normalizePlatformRole = (value?: string | null) => `${value ?? ''}`.trim().toUpperCase()

const hasSourceStoreFlag = (platform: any) =>
  Object.entries(platform ?? {}).some(
    ([key, value]) =>
      key !== 'isConnected' &&
      key !== 'isActive' &&
      key !== 'isRequired' &&
      /^is[A-Z].*Store$/.test(key) &&
      value === true
  )

const isInventoryLogPlatform = (platform: any) => {
  if (!platform || hasSourceStoreFlag(platform)) {
    return false
  }

  const roleValues = [
    platform?.type,
    platform?.nature,
    platform?.platform_info?.type,
    platform?.platform_info?.nature,
  ].map(normalizePlatformRole)

  return !roleValues.some((role) => sourcePlatformRoleValues.has(role))
}

const InventoryLogs: React.FC<InventoryLogsProps> = ({ isProductDetailPage = false, variantId }) => {
     const tempParams = useParams();
     const { platformId } = useParams()
     const routePlatformId = tempParams?.platformId ?? platformId ?? ''
     const [loading, setLoading] = useState(false)
     const dispatch = useAppDispatch()
     const {inventoryLogs, inventoryLogsLoading, inventoryPaginationData, inventoryLocations} =
       useAppSelector((state) => state.inventory)
     const {fetchedConnectedPlatformData,fetchedConnectedPlatformLoading} = useAppSelector((state) => state.fetchConnectedPlatform)
     const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
     const isOmsStore = fetchedUserAccountData?.userAdditionalInfo?.platform_connection_priority === 'OMS'
     const [searchParams] = useSearchParams()
     let sync_status = searchParams.get('sync_status')
     let from_date = searchParams.get('from_date')
     let to_date = searchParams.get('to_date')     
     const [selectedPlatform, setSelectedPlatform] = useState( "")
     const [selectedStarDate, setSelectedStartDate] = useState('')
     const {successToast, errorToast, warningToast} = useAppToast()
     const [selectedEndDate, setSelectedEndDate] = useState('')
     const [showDateRangePicker, setShowDateRangePicker] = useState(false)
     const [searchString, setSearchString] = useState('')
     const resyncableStatuses = ['ERROR', 'ERROR_UNLINKED', 'ERROR_NO_MAPPING']
     const [paginationData, setPaginationData] = useState({
       page_num: 1,
       page_limit: 10,
       search_string: '',
       status: sync_status ? sync_status : '',
       location: '',
    //    shipment_status: '',
       sort_by: 'api_created_at',
       sort_order: 'DESC',
       // api_created_at: '',
       from_date: from_date ? from_date : '',
       to_date: to_date ? to_date : '',
       platform_id: isProductDetailPage ? routePlatformId : selectedPlatform ? selectedPlatform : ''
     })
     const handleResetFilters = () => {
   if (isProductDetailPage) {
     setPaginationData((pre) =>
       ({...pre,
         page_num:1,
         page_limit: 10,
         search_string: '',
         status: '',
         sort_by: 'api_created_at',
         sort_order: 'DESC',
         from_date: '',
         to_date: '',
         location: '',
         platform_id: routePlatformId
       })
     );
       setSelectedStartDate('');
       setSelectedEndDate('');
       setSearchString('');
       return;
       }
   if( fetchedConnectedPlatformData.length >0){
     const initial:any = fetchedConnectedPlatformData.find(isInventoryLogPlatform);

 setPaginationData((pre)=>
          ({...pre, 
            page_num:1,
            page_limit: 10,
            search_string: '',
            status: '',
            sort_by: 'api_created_at',
            sort_order: 'DESC',
            from_date: '',
            to_date: '',
            location: '',
            platform_id: initial?.id ? String(initial.id) : ''
          })
      
      );
       setSelectedPlatform(initial?.id ? String(initial.id) : '');
       setSelectedStartDate('');
       setSelectedEndDate('');
       setSearchString('');
       }

     };

  useEffect(() => {
    dispatch(getAllInventoryLocationsAsync())
  }, [dispatch])

  useEffect(() => {
    if (!inventoryLogsLoading && fetchedConnectedPlatformData.length > 0 && paginationData?.platform_id) {
      dispatch(
        getInventoryLogsAsync({
          paramData: {
            ...paginationData,
            platform_id: isProductDetailPage ? routePlatformId : paginationData?.platform_id,
            variant_id: variantId || (isProductDetailPage ? tempParams?.variantId : undefined),
          }
        })
      );
    }
  }, [paginationData, variantId])

    useEffect(()=> {
       if(isProductDetailPage){
       setPaginationData((pre:any) => {return {...pre, platform_id: routePlatformId }})
       } else if( fetchedConnectedPlatformData.length >0){
     const initial:any = fetchedConnectedPlatformData.find(isInventoryLogPlatform);
       setSelectedPlatform(initial?.id ? String(initial.id) : '')
       setPaginationData((pre:any) => {return {...pre, platform_id: initial?.id ? String(initial.id) : '' }})
       }if( fetchedConnectedPlatformData.length === 0 && fetchedConnectedPlatformLoading === false) {
      warningToast('No connected platforms found. Please connect a platform to view inventory logs.')
       }
    },[fetchedConnectedPlatformData])


    const ResyncButton: FC<{row: any}> = ({row}) => {
      const [isSubmitting, setIsSubmitting] = useState(false)

      const handleResync = async () => {
        setIsSubmitting(true)
        try {
          await dispatch(
            resyncFailedInventoryAsync({
              product_variant_id: row?.updated_product_variant_id ?? null,
              sync_id: row?.sync_id ?? null,
            })
          ).unwrap()
          successToast('Failed inventory sync queued for resync.')
          dispatch(
            getInventoryLogsAsync({
              paramData: {
                ...paginationData,
                platform_id: isProductDetailPage ? routePlatformId : paginationData?.platform_id,
                variant_id: variantId || (isProductDetailPage ? tempParams?.variantId : undefined),
              },
            })
          )
        } catch (error) {
          console.error('Error during inventory resync:', error)
          errorToast('Inventory resync failed. Please try again.')
        } finally {
          setIsSubmitting(false)
        }
      }

      return (
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip>Click to retry syncing this inventory change</Tooltip>}
        >
          <Button
            className='ms-2'
            variant='link'
            style={{padding: '5px 10px'}}
            onClick={handleResync}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faRotate} color='red' size='lg' />
          </Button>
        </OverlayTrigger>
      )
    }



    const handleSort = (ev, sortDirection) => {
      // console.log('ev', ev)
      setPaginationData((pre) => {
        return {...pre, sort_by: ev.sortField, sort_order: (sortDirection + '').toUpperCase()}
      })
    }

  const columns = [
    {
      name: 'Product Name',
      cell: (row) => (
        <div className="d-flex flex-column align-items-center justify-content-center w-100 py-2">
          <p className="mb-1 text-center">{row.product_name}</p>
          {row.parent_variant_sku && (
            <span
              className="fw-semibold mt-1 rounded"
              style={{
                fontSize: '0.75rem',
                padding: '0.25rem 0.5rem',
                background: '#f1f3f5',
                color: '#5e6278',
              }}
            >
              {row.parent_variant_sku}
            </span>
          )}
        </div>
      ),
      center: true,
      width: '200px',
    },
    {
      name: 'SKU',
      cell: (row) => <CommonColumn rowData={row.adj_sku} />,
      sortable: true,
      sortField: 'adj_sku',
      center: true,
    },
    
      {
      name: (
        <div className='d-flex align-items-center justify-content-center gap-2'>
          <span>Source Warehouse</span>
          <Dropdown>
            <Dropdown.Toggle
              as='span'
              className={paginationData.location ? 'text-primary' : 'text-muted'}
              style={{cursor: 'pointer'}}
            >
              <FontAwesomeIcon icon={faFilter} />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{
                strategy: 'fixed',
              }}
              style={{
                zIndex: 9999,
              }}
            >
              <Dropdown.Item
                active={!paginationData.location}
                onClick={() => {
                  setPaginationData((pre) => ({
                    ...pre,
                    page_num: 1,
                    location: '',
                  }))
                }}
              >
                All locations
              </Dropdown.Item>
              {inventoryLocations.map((location) => (
                <Dropdown.Item
                  key={location.id}
                  active={paginationData.location === String(location.id)}
                  onClick={() => {
                    setPaginationData((pre) => ({
                      ...pre,
                      page_num: 1,
                      location: String(location.id),
                    }))
                  }}
                >
                  {location.api_name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
      cell: (row) => <CommonColumn rowData={row.location_name} />,
        center:true,
         width: '180px',
    // sortable: true,
    //   sortField: 'adj_sku',
    
    },
    {
      name: 
              <>
          <div style={{display: "flex", alignItems:"center", justifyContent: "center", gap: "6px"}}>
            <span>Source Channel Qty</span>{' '}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Available stock from the source channel.</Tooltip>}
              >
              <span style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </div>
        </>     
            ,
      cell: (row) => <CommonColumn rowData={row.available_quantity} />,
      center: true,
       width: '200px',
    },
      {
      name: 'Adjustments',
      cell: (row) => <CommonColumn rowData={row.adjustment} />,
        center:true,
         width: '150px',
    // sortable: true,
    //   sortField: 'adj_sku',
    
    },
       {
      name: 

             <>
          <div style={{display: "flex", alignItems:"center", justifyContent: "center", gap: "6px"}}>
            <span>Synced Qty to Destination</span>{' '}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Updated stock level on the destination channel after buffer adjustments.</Tooltip>}
              >
              <span style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </div>
        </> 
            
            ,
      cell: (row) => <CommonColumn rowData={row.updated_quantity} />,
 width: '250px',
      center: true,
    },
    {
      name: 'Status',
      cell: (row) => {
        const showResyncButton =
          resyncableStatuses.includes(row.final_status) && row.is_archived !== true

        return (
          <div className='d-flex align-items-center w-100 justify-content-center'>
            <StatusButton
              syncStatus={row.final_status}
              syncMessage={
                ['ERROR', 'ERROR_UNLINKED', 'ERROR_NO_MAPPING', 'SYNCED_WITH_ERROR'].includes(
                  row.final_status
                )
                  ? row.api_message
                  : ''
              }
            />
            {showResyncButton && <ResyncButton row={row} />}
          </div>
        )
      },
      center: true,
      width: '160px',
    },
    //  {
  //     name: (
  //       <>
  //         <div style={{display: "flex", alignItems:"center", justifyContent: "center", gap: "6px"}}>
  //           <span>Created At</span>{' '}
  //           <OverlayTrigger
  //             placement="top"
  //             overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
  //             >
  //             <span style={{ cursor: 'pointer' }}>
  //               <FontAwesomeIcon icon={faInfoCircle} />
  //             </span>
  //           </OverlayTrigger>
  //         </div>
  //       </>
  //     ),
  //     cell: (row) => {

  //       return (
          
  //             <CommonColumn
  //               rowData={
  //                 row?.adj_created_at
  //                   ? formatDateLocal(row.adj_created_at)
  //                   : ''
  //               }
  //             />
  //       );
  //     },
  //     sortable: true,
  //       sortField: 'api_created_at',
  //     right: true,
  //     center: true,
  //         width: '160px',
  //   },
    {
      name: (
        <>
          <div style={{display: "flex", alignItems:"center", justifyContent: "center", gap: "6px"}}>
            Synced At
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
              >
              <span style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
            </div>
        </>
      ),
      cell: (row) => {
        return (
              <CommonColumn
                rowData={
                  row?.synced_at

                    ? formatDateLocal(row.synced_at)
                    : ''
                }
              />
            
        );
      },
      sortable: true,
      sortField: 'synced_at',
      right: true,
      center: true,
          width: '160px',
    }
  ]
   
  return (
    <>
      {!isProductDetailPage && (
        <div className='mb-3'>
          <span className='fw-bold text-lg ' style={{fontSize: '16px'}}>
            Inventory Logs
          </span>
        </div>
      )}
      <div className='card card-flush'>
        <div className='card-header'>
          <div className='card-title'>
            {!isProductDetailPage && (
              <input
                className='form-control mr-sm-2 input-sm '
                type='search'
                placeholder='Search'
                aria-label='Search'
                onChange={(ev) => {
                  setSearchString(ev.target.value)
                }}
                onKeyDown={(ev) => {
                  if (ev.key == 'Enter') {
                    setPaginationData((pre) => {
                      return {...pre, search_string: searchString, page_num: 1}
                    })
                  }
                }}
                value={searchString}
              />
            )}
          </div>
          <div className='card-toolbar'>
            <div className='d-flex flex-md-row flex-column justify-content-md-end'>
              {!isProductDetailPage && (
                <div className='mx-md-2 my-1 my-md-0'>
                  <select
                    className='form-select'
                    aria-label='Select platform'
                    value={selectedPlatform}
                    onChange={(ev) => {
                      setSelectedPlatform(ev.target.value)

                      setPaginationData((pre) => {
                        // console.log(pre,"old data")
                        return {
                          ...pre,
                          page_num: 1,
                          platform_id: ev.target.value, // Include selectedSyncStatus here
                        }
                      })
                    }}
                  >
                    {fetchedConnectedPlatformData.length ? (
                      fetchedConnectedPlatformData.map((item) => {
                        if (isInventoryLogPlatform(item)) {
                          return (
                            <option key={item?.id + 'platform'} value={item?.id}>
                              {item?.display_name}
                            </option>
                          )
                        }
                      })
                    ) : (
                      <option>Select Channel</option>
                    )}
                  </select>
                </div>
              )}
              {!isOmsStore && (
                <div className='mx-md-2 my-1 my-md-0'>
                  <select
                    className='form-select'
                    aria-label='Select Sync Status'
                    value={paginationData.status}
                    onChange={(ev) => {
                      // setSelectSyncStatus(ev.target.value)
                      setPaginationData((pre) => {
                        return {
                          ...pre,
                          page_num: 1,
                          status: ev.target.value, // Include selectedSyncStatus here
                        }
                      })
                    }}
                  >
                    <option value={''}>Inventory Status</option>
                    <option value={'SYNCED'}>SYNCED</option>
                    <option value={'PENDING'}>PENDING</option>
                    <option value={'PROCESSING'}>PROCESSING</option>
                    <option value={'ERROR'}>ERROR</option>
                    <option value={'PENDING_APPROVAL'}>PENDING APPROVAL</option>
                  </select>
                </div>
              )}
              <div className='mx-md-2 my-1 my-md-0'>
                <button
                  className='btn fs-6 btn-sm bg-white  border text-dark w-250px h-30px align-items-center d-flex justify-content-center '
                  onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                >
                  {selectedStarDate && selectedEndDate
                    ? `${moment(selectedStarDate).format('YYYY/MM/DD')}  -  ${moment(
                        selectedEndDate
                      ).format('YYYY/MM/DD')}`
                    : 'Pick a Date Range'}
                </button>
                <AppDatePickerRange
                  isVisible={showDateRangePicker}
                  handleClose={() => setShowDateRangePicker(false)}
                  onPickRange={({startDate, endDate}) => {
                    // Update the selected start and end dates
                    setSelectedStartDate(startDate)
                    setSelectedEndDate(endDate)
                    // Update pagination data with the selected date range
                    setPaginationData((pre) => ({
                      ...pre,
                      from_date: moment(startDate).format('YYYY-MM-DD'),
                      to_date: moment(endDate).format('YYYY-MM-DD'),
                      page_num: 1,
                    }))
                  }}
                />
              </div>
              <div className='mx-md-2 my-1 my-md-0'>
                <button
                  className='btn  btn-sm btn-primary w-100px fw-bold py-2'
                  onClick={handleResetFilters}
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        </div>
        <TableLoadingShell loading={inventoryLogsLoading}>
        <DataTable
          fixedHeader={true}
          data={inventoryLogs}
          noDataComponent={
            <div className='text-secondary'>
              <h5>No records found</h5>
            </div>
          }
          // data={dummy}
          columns={columns}
          persistTableHead
          onSort={handleSort}
          paginationTotalRows={inventoryPaginationData.count}
          pagination
          paginationServer
          progressPending={false}
          onChangePage={(pageNumber, totalRows) => {
            setPaginationData((pre) => {
              return {...pre, page_num: pageNumber}
            })
          }}
          onChangeRowsPerPage={(currentPage) => {
            setPaginationData((pre) => {
              return {...pre, page_limit: currentPage}
            })
          }}
          customStyles={{
            table: {
              style: {
                height: '55vh',
              },
            },
            headCells: {
              style: {fontWeight: 'bold', fontSize: '14px'},
            },
            cells: {
              style: {
                padding: '10px',
                borderBottom: '1px solid gainsboro',
              },
            },
          }}
        />
        </TableLoadingShell>
      </div>
    </>
  )
}

export default InventoryLogs
